import { useAppToken } from "../../theme/v6/useAppToken";
import React from "react";

import { AimOutlined, EnvironmentOutlined, ReloadOutlined, RobotOutlined, SearchOutlined } from "@ant-design/icons";
import { AdvancedMarker, Map, MapMouseEvent, Pin, useMap } from "@vis.gl/react-google-maps";
import { Alert, Button, Card, Checkbox, Empty, Flex, Grid, Input, Modal, Select, Space, Spin, Tag, Typography, message } from "antd";
import { call, SagaReturnType } from "redux-saga/effects";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import locationResolutionApi from "../../api/apiServices/backOffice/controllers/locationResolution/locationResolution.api";
import { sagaMiddleware } from "../../configureStore";
import { LocationDto } from "../../dto/Location/LocationDto";
import {
  LocationResolutionCandidateDto,
  LocationResolutionResponseDto,
  LocationVerificationStatus,
} from "../../dto/LocationResolution/LocationResolutionDto";
import { LocationType } from "../../dto/Settings/LocationTypeDto";
import { makeSelectLanguage } from "../../containers/App/selectors";
import { createResolutionRequestGuard, shouldOfferAi } from "./requestGuard";

type Props = {
  open: boolean;
  initialQuery?: string;
  onClose: () => void;
  onResolved: (location: LocationDto) => void;
};

type MapPoint = { lat: number; lng: number };

const DEFAULT_MAP_CENTER: MapPoint = { lat: 50, lng: 30 };
const MAP_ID = "fbcbfa15d9bf48044c08c792";

const samePoint = (first?: MapPoint, second?: MapPoint) =>
  first !== undefined && second !== undefined && first.lat === second.lat && first.lng === second.lng;

const CandidateMapCamera = ({ origin, candidate, zoom }: { origin?: MapPoint; candidate?: MapPoint; zoom: number }) => {
  const map = useMap();
  const originLatitude = origin?.lat;
  const originLongitude = origin?.lng;
  const candidateLatitude = candidate?.lat;
  const candidateLongitude = candidate?.lng;
  React.useEffect(() => {
    if (!map) return;
    if (
      originLatitude !== undefined &&
      originLongitude !== undefined &&
      candidateLatitude !== undefined &&
      candidateLongitude !== undefined &&
      (originLatitude !== candidateLatitude || originLongitude !== candidateLongitude)
    ) {
      map.fitBounds(
        {
          north: Math.max(originLatitude, candidateLatitude),
          south: Math.min(originLatitude, candidateLatitude),
          east: Math.max(originLongitude, candidateLongitude),
          west: Math.min(originLongitude, candidateLongitude),
        },
        64,
      );
      return;
    }
    const latitude = candidateLatitude ?? originLatitude;
    const longitude = candidateLongitude ?? originLongitude;
    if (latitude !== undefined && longitude !== undefined) {
      map.panTo({ lat: latitude, lng: longitude });
      map.setZoom(zoom);
    }
  }, [candidateLatitude, candidateLongitude, map, originLatitude, originLongitude, zoom]);
  return null;
};

type LocationResolutionStatusCopy = {
  existing: string;
  external: string;
  ai: string;
  pin: string;
};

const statusColor: Record<LocationVerificationStatus, string> = {
  VERIFIED_EXISTING: "green",
  VERIFIED_EXTERNAL: "blue",
  USER_CONFIRMED_AI: "gold",
  USER_CONFIRMED_PIN: "purple",
};

const statusLabel = (status: LocationVerificationStatus, copy: LocationResolutionStatusCopy) =>
  ({
    VERIFIED_EXISTING: copy.existing,
    VERIFIED_EXTERNAL: copy.external,
    USER_CONFIRMED_AI: copy.ai,
    USER_CONFIRMED_PIN: copy.pin,
  }[status]);

const LocationResolutionModal = React.memo(({ open, initialQuery, onClose, onResolved }: Props) => {
  const language = useSelector(makeSelectLanguage()) || "ru";
  const { t } = useTranslation("translations");
  const copy = {
    title: t("location_resolution_title"),
    intro: t("location_resolution_intro"),
    queryPlaceholder: t("location_resolution_query_placeholder"),
    search: t("location_resolution_search"),
    retry: t("location_resolution_retry"),
    type: t("location_resolution_type"),
    city: t("location_resolution_city"),
    port: t("location_resolution_port"),
    airport: t("location_resolution_airport"),
    railway: t("location_resolution_railway"),
    results: t("location_resolution_results"),
    empty: t("location_resolution_empty"),
    aiHelpTitle: t("location_resolution_ai_help_title"),
    aiHelpDescription: t("location_resolution_ai_help_description"),
    askAi: t("location_resolution_ask_ai"),
    manual: t("location_resolution_manual"),
    backToResults: t("location_resolution_back_to_results"),
    mapHint: t("location_resolution_map_hint"),
    reverse: t("location_resolution_reverse"),
    choosePoint: t("location_resolution_choose_point"),
    existing: t("location_resolution_existing"),
    external: t("location_resolution_external"),
    selectedByYou: t("location_resolution_selected_by_you"),
    ai: t("location_resolution_ai"),
    pin: t("location_resolution_pin"),
    coordinates: t("location_resolution_coordinates"),
    confirmResponsibility: t("location_resolution_confirm_responsibility"),
    cancel: t("location_resolution_cancel"),
    confirm: t("location_resolution_confirm"),
    error: t("location_resolution_error"),
    confirmError: t("location_resolution_confirm_error"),
    pinSelected: t("location_resolution_pin_selected"),
    networkError: t("location_resolution_network_error"),
    timeoutError: t("location_resolution_timeout_error"),
    accessError: t("location_resolution_access_error"),
    rateLimitError: t("location_resolution_rate_limit_error"),
    invalidResponseError: t("location_resolution_invalid_response_error"),
    unavailableError: t("location_resolution_unavailable_error"),
    coordinateConflict: t("location_resolution_coordinate_conflict"),
    coordinateCheckUnavailable: t("location_resolution_coordinate_check_unavailable"),
  };
  const screens = Grid.useBreakpoint();
  const { token } = useAppToken();

  const [query, setQuery] = React.useState("");
  const [locationTypeHint, setLocationTypeHint] = React.useState<Exclude<LocationType, "UNKNOWN">>();
  const [result, setResult] = React.useState<LocationResolutionResponseDto>();
  const [selectedToken, setSelectedToken] = React.useState<string>();
  const [manualMode, setManualMode] = React.useState(false);
  const [manualPoint, setManualPoint] = React.useState<MapPoint>();
  const [manualLookupDone, setManualLookupDone] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const initializedForOpenRef = React.useRef(false);
  const lastRequestForceAiRef = React.useRef(false);
  const requestGuard = React.useRef(createResolutionRequestGuard()).current;

  const selectedCandidate = React.useMemo(
    () => result?.candidates.find((candidate) => candidate.candidateToken === selectedToken),
    [result, selectedToken],
  );

  const candidatePoint = selectedCandidate ? { lat: selectedCandidate.latitude, lng: selectedCandidate.longitude } : undefined;
  const conflictingCandidates = result?.noticeCode === "COORDINATE_CONFLICT" ? result.candidates : [];
  const conflictPoints = conflictingCandidates.map((candidate) => ({ lat: candidate.latitude, lng: candidate.longitude }));
  const originPoint = manualMode ? manualPoint : undefined;
  const mapPoint = originPoint || candidatePoint;
  const offerAi = shouldOfferAi({
    hasResult: result !== undefined,
    candidateCount: result?.candidates.length ?? 0,
    llmUsed: result?.llmUsed ?? false,
    aiFailed: result?.failure?.stage === "AI",
    manualMode,
    manualLookupDone,
  });

  const failure = result?.failure;
  const failureText = failure
    ? {
        NETWORK: copy.networkError,
        TIMEOUT: copy.timeoutError,
        ACCESS: copy.accessError,
        RATE_LIMIT: copy.rateLimitError,
        INVALID_RESPONSE: copy.invalidResponseError,
        UNAVAILABLE: copy.unavailableError,
      }[failure.code] || copy.unavailableError
    : undefined;

  const applyResult = React.useCallback((nextResult: LocationResolutionResponseDto) => {
    setResult(nextResult);
    setSelectedToken(nextResult.candidates.length === 1 ? nextResult.candidates[0].candidateToken : undefined);
    setConfirmed(false);
    setError(undefined);
  }, []);

  const invalidateCandidates = React.useCallback(() => {
    requestGuard.invalidate();
    setResult((previous) => (previous ? { ...previous, candidates: [], failure: undefined, noticeCode: undefined } : undefined));
    setSelectedToken(undefined);
    setConfirmed(false);
    setError(undefined);
  }, [requestGuard]);

  const changePoint = React.useCallback(
    (point: MapPoint) => {
      if (confirming) return;
      invalidateCandidates();
      lastRequestForceAiRef.current = false;
      setManualPoint(point);
      setManualLookupDone(false);
    },
    [confirming, invalidateCandidates],
  );

  const selectCandidate = React.useCallback(
    (candidate: LocationResolutionCandidateDto) => {
      if (loading || confirming) return;
      setSelectedToken(candidate.candidateToken);
      if (!manualMode) {
        setManualMode(candidate.verificationStatus === "USER_CONFIRMED_PIN");
        setManualPoint({ lat: candidate.latitude, lng: candidate.longitude });
      }
      setConfirmed(false);
    },
    [confirming, loading, manualMode],
  );

  const search = React.useCallback(
    async (nextQuery: string, forceAi = false) => {
      const normalizedQuery = nextQuery.trim();
      if (normalizedQuery.length < 2 || confirming) {
        return;
      }
      const request = requestGuard.begin();
      if (request === undefined) return;
      lastRequestForceAiRef.current = forceAi;
      setLoading(true);
      setError(undefined);
      setManualMode(false);
      setManualPoint(undefined);
      setManualLookupDone(false);
      setResult(undefined);
      setSelectedToken(undefined);
      setConfirmed(false);
      try {
        const apiResult: SagaReturnType<typeof locationResolutionApi.search> = await sagaMiddleware
          .run(function* () {
            return yield call([locationResolutionApi, locationResolutionApi.search], {
              query: normalizedQuery,
              locationTypeHint,
              forceAi,
            });
          })
          .toPromise();
        if (!requestGuard.isCurrent(request)) return;
        if (apiResult.success) {
          applyResult(apiResult.success.payload);
        } else {
          setError(copy.error);
        }
      } catch {
        if (requestGuard.isCurrent(request)) setError(copy.error);
      } finally {
        if (requestGuard.finish(request)) setLoading(false);
      }
    },
    [applyResult, confirming, copy.error, locationTypeHint, requestGuard],
  );

  React.useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      requestGuard.reset();
      setLoading(false);
      setConfirming(false);
      return;
    }
    if (initializedForOpenRef.current) {
      return;
    }
    initializedForOpenRef.current = true;
    const nextQuery = initialQuery?.trim() || "";
    setQuery(nextQuery);
    setResult(undefined);
    setSelectedToken(undefined);
    setManualMode(false);
    setManualPoint(undefined);
    setManualLookupDone(false);
    setConfirmed(false);
    setError(undefined);
    if (nextQuery.length >= 2) {
      void search(nextQuery);
    }
  }, [initialQuery, open, requestGuard, search]);

  React.useEffect(
    () => () => {
      requestGuard.reset();
      initializedForOpenRef.current = false;
    },
    [requestGuard],
  );

  const close = React.useCallback(() => {
    if (confirming) return;
    requestGuard.reset();
    onClose();
  }, [confirming, onClose, requestGuard]);

  const reverseGeocode = React.useCallback(
    async (forceAi = false) => {
      if (!result || !manualPoint || confirming) {
        return;
      }
      const request = requestGuard.begin();
      if (request === undefined) return;
      lastRequestForceAiRef.current = forceAi;
      setLoading(true);
      setManualLookupDone(false);
      setResult((previous) => (previous ? { ...previous, candidates: [], failure: undefined, noticeCode: undefined } : undefined));
      setSelectedToken(undefined);
      setConfirmed(false);
      setError(undefined);
      try {
        const apiResult: SagaReturnType<typeof locationResolutionApi.reverseGeocode> = await sagaMiddleware
          .run(function* () {
            return yield call([locationResolutionApi, locationResolutionApi.reverseGeocode], result.resolutionId, {
              latitude: manualPoint.lat,
              longitude: manualPoint.lng,
              locationTypeHint,
              forceAi,
            });
          })
          .toPromise();
        if (!requestGuard.isCurrent(request)) return;
        if (apiResult.success) {
          applyResult(apiResult.success.payload);
          setManualMode(true);
          setManualLookupDone(true);
        } else {
          setError(copy.error);
        }
      } catch {
        if (requestGuard.isCurrent(request)) setError(copy.error);
      } finally {
        if (requestGuard.finish(request)) setLoading(false);
      }
    },
    [applyResult, confirming, copy.error, locationTypeHint, manualPoint, requestGuard, result],
  );

  const confirmLocation = React.useCallback(async () => {
    if (!result || !selectedCandidate || !confirmed || loading || confirming) {
      return;
    }
    const request = requestGuard.begin();
    if (request === undefined) return;
    setConfirming(true);
    try {
      const apiResult: SagaReturnType<typeof locationResolutionApi.confirm> = await sagaMiddleware
        .run(function* () {
          return yield call([locationResolutionApi, locationResolutionApi.confirm], result.resolutionId, {
            candidateToken: selectedCandidate.candidateToken,
            confirmed: true,
          });
        })
        .toPromise();
      if (!requestGuard.isCurrent(request)) return;
      if (apiResult.success) {
        onResolved(apiResult.success.payload);
        onClose();
      } else {
        message.error(apiResult.reject?.payload?.message || copy.confirmError);
      }
    } catch {
      if (requestGuard.isCurrent(request)) message.error(copy.confirmError);
    } finally {
      if (requestGuard.finish(request)) setConfirming(false);
    }
  }, [confirmed, confirming, copy.confirmError, loading, onClose, onResolved, requestGuard, result, selectedCandidate]);

  const handleMapClick = React.useCallback(
    (event: MapMouseEvent) => {
      if (!manualMode || !event.detail.latLng) {
        return;
      }
      changePoint(event.detail.latLng);
    },
    [changePoint, manualMode],
  );

  const typeOptions = [
    { value: "CITY", label: copy.city },
    { value: "PORT", label: copy.port },
    { value: "AIRPORT", label: copy.airport },
    { value: "RAILWAY", label: copy.railway },
  ];

  return (
    <Modal
      open={open}
      title={copy.title}
      width={980}
      onCancel={close}
      closable={!confirming}
      keyboard={!confirming}
      mask={{ closable: !confirming }}
      destroyOnHidden
      footer={
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Button onClick={close} disabled={confirming}>
            {copy.cancel}
          </Button>
          <Button type="primary" loading={confirming} disabled={!selectedCandidate || !confirmed || loading} onClick={confirmLocation}>
            {copy.confirm}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <Alert type="info" showIcon title={copy.intro} />

        <Flex gap={8} wrap>
          <Input
            value={query}
            disabled={confirming}
            prefix={<SearchOutlined />}
            placeholder={copy.queryPlaceholder}
            onChange={(event) => {
              invalidateCandidates();
              lastRequestForceAiRef.current = false;
              setQuery(event.target.value);
              setResult(undefined);
              setManualMode(false);
              setManualPoint(undefined);
              setManualLookupDone(false);
            }}
            onPressEnter={() => void search(query)}
            style={{ flex: "1 1 340px" }}
          />
          <Select
            allowClear
            disabled={confirming}
            value={locationTypeHint}
            placeholder={copy.type}
            options={typeOptions}
            onChange={(nextType) => {
              invalidateCandidates();
              lastRequestForceAiRef.current = false;
              setLocationTypeHint(nextType);
              setManualLookupDone(false);
              if (!manualMode) setResult(undefined);
            }}
            style={{ minWidth: 210, flex: "0 1 230px" }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={loading}
            disabled={query.trim().length < 2 || confirming}
            onClick={() => void search(query)}
          >
            {result ? copy.retry : copy.search}
          </Button>
        </Flex>

        {(error || failureText) && (
          <Alert
            type="error"
            showIcon
            title={error || failureText}
            description={failure ? `${failure.stage} · ${failure.code} · ${failure.reference}` : undefined}
            action={
              <Button
                size="small"
                disabled={loading || confirming}
                icon={<ReloadOutlined />}
                onClick={() =>
                  void (manualMode ? reverseGeocode(lastRequestForceAiRef.current) : search(query, lastRequestForceAiRef.current))
                }
              >
                {copy.retry}
              </Button>
            }
          />
        )}

        {result?.noticeCode && !loading && (
          <Alert
            type="warning"
            showIcon
            title={result.noticeCode === "COORDINATE_CONFLICT" ? copy.coordinateConflict : copy.coordinateCheckUnavailable}
          />
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: screens.lg ? "minmax(320px, 0.9fr) minmax(420px, 1.1fr)" : "1fr",
            gap: 16,
          }}
        >
          <Flex vertical gap={10} style={{ minWidth: 0 }}>
            <Flex justify="space-between" align="center" gap={8}>
              <Typography.Text strong>{copy.results}</Typography.Text>
              {result?.manualPinAvailable && (
                <Button
                  size="small"
                  icon={<AimOutlined />}
                  disabled={loading || confirming}
                  onClick={() => {
                    if (manualMode) {
                      void search(query);
                    } else {
                      invalidateCandidates();
                      setManualPoint(mapPoint);
                      setManualLookupDone(false);
                      setManualMode(true);
                    }
                  }}
                >
                  {manualMode ? copy.backToResults : copy.manual}
                </Button>
              )}
            </Flex>

            {manualMode && (
              <Alert
                type="warning"
                showIcon
                title={copy.mapHint}
                description={
                  locationTypeHint ? (
                    <Space size={6} wrap>
                      <Typography.Text type="secondary">{copy.type}:</Typography.Text>
                      <Tag>{typeOptions.find((option) => option.value === locationTypeHint)?.label}</Tag>
                    </Space>
                  ) : undefined
                }
              />
            )}

            <Spin spinning={loading}>
              {result && !failure && !loading && result.candidates.length === 0 && (!manualMode || manualLookupDone) && (
                <Empty description={copy.empty} />
              )}
              {!result && !loading && !error && <Empty description={copy.intro} />}
              <Flex vertical gap={8}>
                {result?.candidates.map((candidate) => {
                  const selected = candidate.candidateToken === selectedToken;
                  const displayName = language.startsWith("zh")
                    ? candidate.chineseName || candidate.englishName || candidate.name
                    : language === "ru"
                    ? candidate.name
                    : candidate.englishName || candidate.name;
                  const country =
                    language === "ru"
                      ? candidate.countryName || candidate.countryEnglishName
                      : candidate.countryEnglishName || candidate.countryName;
                  const candidateType = typeOptions.find((option) => option.value === candidate.type)?.label;
                  const userSelectedType =
                    candidateType && candidate.type === locationTypeHint && candidate.verificationStatus === "USER_CONFIRMED_PIN"
                      ? `${candidateType} (${copy.selectedByYou})`
                      : candidateType;
                  return (
                    <Card
                      key={candidate.candidateToken}
                      size="small"
                      hoverable
                      role="button"
                      tabIndex={loading || confirming ? -1 : 0}
                      aria-pressed={selected}
                      aria-disabled={loading || confirming}
                      onClick={() => selectCandidate(candidate)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectCandidate(candidate);
                        }
                      }}
                      style={{ borderColor: selected ? token.colorPrimary : token.colorBorderSecondary }}
                    >
                      <Flex vertical gap={6}>
                        <Flex justify="space-between" align="start" gap={8}>
                          <Space size={6}>
                            <EnvironmentOutlined style={{ color: selected ? token.colorPrimary : undefined }} />
                            <Typography.Text strong>{displayName}</Typography.Text>
                          </Space>
                          <Tag color={statusColor[candidate.verificationStatus]}>{statusLabel(candidate.verificationStatus, copy)}</Tag>
                        </Flex>
                        <Typography.Text type="secondary">
                          {[country, candidate.regionName, userSelectedType].filter(Boolean).join(" · ")}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {copy.coordinates}: {candidate.latitude.toFixed(5)}, {candidate.longitude.toFixed(5)}
                        </Typography.Text>
                        {candidate.warnings.map((warning) => (
                          <Alert key={warning} type="warning" showIcon title={warning} />
                        ))}
                        {candidate.attribution && (
                          <Typography.Link
                            href="https://www.openstreetmap.org/copyright"
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12 }}
                          >
                            {candidate.attribution}
                          </Typography.Link>
                        )}
                        {candidate.sourceUrl?.startsWith("https://") && (
                          <Typography.Link
                            href={candidate.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, overflowWrap: "anywhere" }}
                          >
                            {candidate.sourceUrl}
                          </Typography.Link>
                        )}
                      </Flex>
                    </Card>
                  );
                })}
              </Flex>
            </Spin>

            {offerAi && (
              <Alert
                type="info"
                showIcon
                title={copy.aiHelpTitle}
                description={copy.aiHelpDescription}
                action={
                  <Button
                    icon={<RobotOutlined />}
                    loading={loading}
                    disabled={(manualMode ? !manualPoint : query.trim().length < 2) || confirming}
                    onClick={() => void (manualMode ? reverseGeocode(true) : search(query, true))}
                  >
                    {copy.askAi}
                  </Button>
                }
              />
            )}

            {manualMode && (
              <Flex vertical gap={6}>
                <Button
                  type="primary"
                  ghost
                  icon={<AimOutlined />}
                  disabled={!manualPoint || !result || confirming}
                  loading={loading}
                  onClick={() => void reverseGeocode()}
                >
                  {copy.reverse}
                </Button>
                {!manualPoint && <Typography.Text type="secondary">{copy.choosePoint}</Typography.Text>}
              </Flex>
            )}
          </Flex>

          <div style={{ minHeight: screens.lg ? 440 : 320 }}>
            <Map
              mapId={MAP_ID}
              defaultCenter={DEFAULT_MAP_CENTER}
              defaultZoom={3}
              disableDefaultUI
              zoomControl
              gestureHandling="greedy"
              onClick={handleMapClick}
              style={{ height: "100%", minHeight: screens.lg ? 440 : 320, borderRadius: token.borderRadiusLG, overflow: "hidden" }}
            >
              <CandidateMapCamera
                origin={originPoint || conflictPoints[0]}
                candidate={conflictPoints[1] || candidatePoint}
                zoom={selectedCandidate?.type === "CITY" ? 10 : 15}
              />
              {conflictingCandidates.map((candidate) => (
                <AdvancedMarker
                  key={candidate.candidateToken}
                  position={{ lat: candidate.latitude, lng: candidate.longitude }}
                  title={candidate.englishName || candidate.name}
                  onClick={() => selectCandidate(candidate)}
                >
                  <Pin
                    background={candidate.verificationStatus === "USER_CONFIRMED_AI" ? token.colorWarning : token.colorPrimary}
                    borderColor={token.colorBgContainer}
                    glyphColor={token.colorBgContainer}
                  />
                </AdvancedMarker>
              ))}
              {originPoint && (
                <AdvancedMarker
                  position={originPoint}
                  draggable={!confirming}
                  onDragEnd={(event) => {
                    const nextPoint = event.latLng?.toJSON();
                    if (nextPoint) {
                      changePoint(nextPoint);
                    }
                  }}
                >
                  <Pin background="#722ed1" borderColor="#ffffff" glyphColor="#ffffff" />
                </AdvancedMarker>
              )}
              {candidatePoint && conflictingCandidates.length === 0 && !samePoint(originPoint, candidatePoint) && (
                <AdvancedMarker position={candidatePoint}>
                  <Pin background={token.colorPrimary} borderColor="#ffffff" glyphColor="#ffffff" />
                </AdvancedMarker>
              )}
            </Map>
          </div>
        </div>

        {selectedCandidate?.verificationStatus === "USER_CONFIRMED_PIN" && (
          <Alert type="success" showIcon title={statusLabel(selectedCandidate.verificationStatus, copy)} description={copy.pinSelected} />
        )}
        <Checkbox
          checked={confirmed}
          disabled={!selectedCandidate || loading || confirming}
          onChange={(event) => setConfirmed(event.target.checked)}
        >
          {copy.confirmResponsibility}
        </Checkbox>
      </Flex>
    </Modal>
  );
});

export default LocationResolutionModal;
